import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormLabel,
  InputAdornment,
  MenuItem,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { hostApiClient } from '../../hostApiClient';
import { tutorApiClient } from '../../tutorApiClient';
import {
  LESSON_OPTION_DURATION_MINUTES,
  LessonOptionDetailResponse,
  LessonOptionDurationMinutes,
  LessonOptionSessionType,
  LessonOptionStatus,
  LessonOptionSummaryResponse,
  LessonOptionUpsertRequest,
} from '../../models/LessonOption';
import { OrganizationType } from '../../models/Organization';
import { HostTutorResponse } from '../../models/Tutor';
import { hostTutorDisplayName } from '../../utils/hostTutorDisplay';
import {
  extractTutorProfileValidationMessage,
  getTutorSessionTypeLabel,
  isTutorSessionType,
  normalizeTutorSessionType,
} from '../../utils/tutorSessionType';
import { tutorAvatarInitials } from '../../utils/tutorAvatarInitials';
import { NotificationSnackbar } from '../../components/common/NotificationSnackbar';
import HostTutorSpaceAssignmentField from '../../components/host/HostTutorSpaceAssignmentField';
import LessonOptionStatusChip from '../../components/host/LessonOptionStatusChip';
import type { LessonOptionDuplicateLocationState } from '../../utils/lessonOptionDuplicate';

const FALLBACK_CURRENCY = 'PLN';

type FormField = 'name' | 'durationMinutes' | 'price' | 'sessionType';

type LessonOptionFormState = {
  name: string;
  description: string;
  durationMinutes: LessonOptionDurationMinutes | '';
  price: string;
  currencyCode: string;
  sessionType: LessonOptionSessionType | '';
};

const emptyForm: LessonOptionFormState = {
  name: '',
  description: '',
  durationMinutes: 60,
  price: '',
  currencyCode: FALLBACK_CURRENCY,
  sessionType: '',
};

const isAssignableTutorStatus = (status?: string) => {
  const key = (status || 'ACTIVE').toUpperCase();
  return key === 'ACTIVE' || key === 'INVITED';
};

const HostLessonOptionFormPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const isEditMode = Boolean(id);

  const [form, setForm] = useState<LessonOptionFormState>(emptyForm);
  const [status, setStatus] = useState<LessonOptionStatus | null>(null);
  const [assignedTutorIds, setAssignedTutorIds] = useState<string[]>([]);
  const [persistedAssignedTutorIds, setPersistedAssignedTutorIds] = useState<string[]>([]);
  const [organizationType, setOrganizationType] = useState<OrganizationType | undefined>();
  const [selfTutorId, setSelfTutorId] = useState<string | null>(null);
  const [tutors, setTutors] = useState<HostTutorResponse[]>([]);
  const [existingOptions, setExistingOptions] = useState<LessonOptionSummaryResponse[]>([]);
  const [loading, setLoading] = useState(isEditMode);
  const [tutorsLoading, setTutorsLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingMode, setSavingMode] = useState<'draft' | 'activate' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [touched, setTouched] = useState<Partial<Record<FormField, boolean>>>({});
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  useEffect(() => {
    const state = location.state as {
      flashMessage?: string;
      flashSeverity?: 'success' | 'error';
    } | null;
    if (state?.flashMessage) {
      setSnackbar({
        open: true,
        message: state.flashMessage,
        severity: state.flashSeverity === 'error' ? 'error' : 'success',
      });
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location.pathname, location.state, navigate]);

  useEffect(() => {
    if (id) {
      return;
    }

    const duplicateFrom = (location.state as LessonOptionDuplicateLocationState | null)?.duplicateFrom;
    if (!duplicateFrom) {
      return;
    }

    setForm({
      name: duplicateFrom.name,
      description: duplicateFrom.description ?? '',
      durationMinutes: LESSON_OPTION_DURATION_MINUTES.includes(
        duplicateFrom.durationMinutes as LessonOptionDurationMinutes
      )
        ? (duplicateFrom.durationMinutes as LessonOptionDurationMinutes)
        : '',
      price: String(duplicateFrom.price),
      currencyCode: duplicateFrom.currencyCode || FALLBACK_CURRENCY,
      sessionType: normalizeTutorSessionType(duplicateFrom.sessionType) ?? '',
    });
    setStatus(null);
    setAssignedTutorIds(duplicateFrom.assignedTutorIds ?? []);
    setPersistedAssignedTutorIds([]);
    navigate(location.pathname, { replace: true, state: null });
  }, [id, location.pathname, location.state, navigate]);

  const isIndividualTutorOrg = organizationType === OrganizationType.INDIVIDUAL_TUTOR;

  useEffect(() => {
    let cancelled = false;
    void hostApiClient
      .getOrganization()
      .then((organization) => {
        if (!cancelled) {
          setOrganizationType(organization.organizationType);
        }
      })
      .catch((err) => {
        console.error('Error fetching organization type for lesson option form:', err);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!isIndividualTutorOrg) {
      return;
    }

    let cancelled = false;
    void tutorApiClient
      .getTutorProfileSettings()
      .then((profile) => {
        if (!cancelled) {
          setSelfTutorId(profile.tutorId);
        }
      })
      .catch((err) => {
        console.error('Error fetching tutor profile for lesson option self-assignment:', err);
      });
    return () => {
      cancelled = true;
    };
  }, [isIndividualTutorOrg]);

  useEffect(() => {
    if (!isIndividualTutorOrg || !selfTutorId) {
      return;
    }
    setAssignedTutorIds([selfTutorId]);
  }, [isIndividualTutorOrg, selfTutorId]);

  useEffect(() => {
    if (isIndividualTutorOrg) {
      return;
    }

    const fetchTutors = async () => {
      try {
        setTutorsLoading(true);
        const data = await tutorApiClient.getMyTutors();
        setTutors(data);
      } catch (err) {
        console.error('Error fetching tutors for lesson option assignment:', err);
      } finally {
        setTutorsLoading(false);
      }
    };

    fetchTutors();
  }, [isIndividualTutorOrg]);

  useEffect(() => {
    const fetchExistingOptions = async () => {
      try {
        const offers = await hostApiClient.listLessonOptions();
        let archived: LessonOptionSummaryResponse[] = [];
        try {
          archived = await hostApiClient.listLessonOptions(['ARCHIVED']);
        } catch (archivedErr) {
          console.warn('Error fetching archived lesson options for duplicate-name check:', archivedErr);
        }
        setExistingOptions([...offers, ...archived]);
      } catch (err) {
        console.error('Error fetching lesson options for duplicate-name check:', err);
      }
    };

    fetchExistingOptions();
  }, []);

  useEffect(() => {
    if (!id) {
      return;
    }

    const fetchOption = async () => {
      try {
        setLoading(true);
        const data = await hostApiClient.getLessonOption(id);
        applyDetail(data);
        setError(null);
      } catch (err) {
        console.error(`Error fetching host lesson option ${id}:`, err);
        setError(t('hostLessonOptions.fetchDetailError', 'Failed to load this lesson option'));
      } finally {
        setLoading(false);
      }
    };

    fetchOption();
  }, [id, t]);

  const applyDetail = (data: LessonOptionDetailResponse) => {
    setForm({
      name: data.name,
      description: data.description ?? '',
      durationMinutes: LESSON_OPTION_DURATION_MINUTES.includes(
        data.durationMinutes as LessonOptionDurationMinutes
      )
        ? (data.durationMinutes as LessonOptionDurationMinutes)
        : '',
      price: String(data.price),
      currencyCode: data.currencyCode || FALLBACK_CURRENCY,
      sessionType: normalizeTutorSessionType(data.sessionType) ?? '',
    });
    setStatus(data.status);
    const tutorIds = data.assignedTutorIds ?? [];
    setAssignedTutorIds(tutorIds);
    setPersistedAssignedTutorIds(tutorIds);
  };

  const syncTutorAssignments = async (
    lessonOptionId: string,
    nextTutorIds: string[],
    previousTutorIds: string[]
  ): Promise<LessonOptionDetailResponse | null> => {
    const previous = new Set(previousTutorIds);
    const next = new Set(nextTutorIds);
    const toAssign = nextTutorIds.filter((tutorId) => !previous.has(tutorId));
    const toUnassign = previousTutorIds.filter((tutorId) => !next.has(tutorId));

    let latest: LessonOptionDetailResponse | null = null;
    for (const tutorId of toAssign) {
      latest = await hostApiClient.assignLessonOptionTutor(lessonOptionId, tutorId);
    }
    for (const tutorId of toUnassign) {
      latest = await hostApiClient.unassignLessonOptionTutor(lessonOptionId, tutorId);
    }
    return latest;
  };

  const fieldErrors = useMemo(() => {
    const priceValue = Number(form.price);
    return {
      name: form.name.trim()
        ? null
        : t('hostLessonOptions.validationName', 'Enter a lesson option name'),
      durationMinutes:
        form.durationMinutes === ''
          ? t('hostLessonOptions.validationDuration', 'Choose a duration')
          : null,
      price:
        form.price.trim() === '' || !Number.isFinite(priceValue) || priceValue < 0
          ? t('hostLessonOptions.validationPrice', 'Enter a price of 0 or more')
          : null,
      sessionType: isTutorSessionType(form.sessionType)
        ? null
        : t('hostLessonOptions.validationSessionType', 'Choose online or in person'),
    };
  }, [form, t]);

  const duplicateWarning = useMemo(() => {
    const trimmedName = form.name.trim();
    if (!trimmedName || !isTutorSessionType(form.sessionType)) return null;
    const isDuplicate = existingOptions.some(
      (option) =>
        option.id !== id &&
        option.name.trim().toLocaleLowerCase() === trimmedName.toLocaleLowerCase() &&
        option.sessionType === form.sessionType
    );
    return isDuplicate
      ? t(
          'hostLessonOptions.duplicateNameSessionType',
          'A lesson option with this name and session type already exists'
        )
      : null;
  }, [existingOptions, form.name, form.sessionType, id, t]);

  const [showDuplicateWarning, setShowDuplicateWarning] = useState(false);

  useEffect(() => {
    setShowDuplicateWarning(false);
  }, [form.name, form.sessionType]);

  const shouldShowFieldError = (field: FormField) =>
    Boolean((touched[field] || submitAttempted) && fieldErrors[field]);

  const toUpsertRequest = (): LessonOptionUpsertRequest | null => {
    if (fieldErrors.name || fieldErrors.durationMinutes || fieldErrors.price || fieldErrors.sessionType) {
      return null;
    }
    return {
      name: form.name.trim(),
      description: form.description.trim() || null,
      durationMinutes: form.durationMinutes as LessonOptionDurationMinutes,
      price: Number(form.price),
      currencyCode: form.currencyCode.trim() || FALLBACK_CURRENCY,
      sessionType: form.sessionType as LessonOptionSessionType,
    };
  };

  const effectiveAssignedTutorIds =
    isIndividualTutorOrg && selfTutorId ? [selfTutorId] : assignedTutorIds;

  const handleSave = async (mode: 'draft' | 'activate') => {
    setSubmitAttempted(true);

    if (isIndividualTutorOrg && !selfTutorId) {
      setError(
        t(
          'hostLessonOptions.selfTutorUnavailable',
          'Could not load your tutor profile. Try again in a moment.'
        )
      );
      return;
    }

    if (status === 'ARCHIVED') {
      setShowDuplicateWarning(false);
      setError(
        t(
          'hostLessonOptions.archivedReadOnlyHint',
          'This option is archived and can’t be edited. Manage it from the Archive tab.'
        )
      );
      return;
    }

    const request = toUpsertRequest();
    if (!request) {
      setShowDuplicateWarning(false);
      setError(t('hostLessonOptions.validationError', 'Please fill in all required fields'));
      return;
    }
    if (duplicateWarning) {
      setError(null);
      setShowDuplicateWarning(true);
      return;
    }
    setShowDuplicateWarning(false);

    try {
      setSaving(true);
      setSavingMode(mode);
      setError(null);

      if (isEditMode && id) {
        const previousStatus = status;
        let updated = await hostApiClient.updateLessonOption(id, request);
        try {
          const assignmentResult = await syncTutorAssignments(
            id,
            effectiveAssignedTutorIds,
            persistedAssignedTutorIds
          );
          if (assignmentResult) {
            updated = assignmentResult;
          } else {
            updated = { ...updated, assignedTutorIds: effectiveAssignedTutorIds };
          }
        } catch (assignmentError) {
          try {
            updated = await hostApiClient.getLessonOption(id);
            applyDetail(updated);
          } catch (refetchError) {
            console.error(`Error refetching lesson option ${id} after assignment failure:`, refetchError);
          }
          throw assignmentError;
        }

        if (mode === 'activate') {
          if (previousStatus !== 'ACTIVE') {
            await hostApiClient.activateLessonOption(id);
          }
          navigate(`/host/lesson-options/${id}`, {
            replace: true,
            state: {
              flashMessage:
                previousStatus !== 'ACTIVE'
                  ? t('hostLessonOptions.activatedSuccess', 'Lesson option activated')
                  : t('hostLessonOptions.updatedSuccess', 'Lesson option saved'),
            },
          });
          return;
        }

        if (previousStatus === 'ACTIVE') {
          await hostApiClient.deactivateLessonOption(id);
        }
        navigate(`/host/lesson-options/${id}`, {
          replace: true,
          state: {
            flashMessage:
              previousStatus === 'ACTIVE'
                ? t('hostLessonOptions.deactivatedSuccess', 'Lesson option moved to draft')
                : t('hostLessonOptions.savedAsDraft', 'Lesson option saved as draft'),
          },
        });
        return;
      }

      const created = await hostApiClient.createLessonOption(request);
      let assignmentFailed = false;
      try {
        await syncTutorAssignments(created.id, effectiveAssignedTutorIds, []);
      } catch (assignmentError) {
        console.error(`Error assigning tutors to new lesson option ${created.id}:`, assignmentError);
        assignmentFailed = true;
      }

      if (assignmentFailed) {
        navigate(`/host/lesson-options/${created.id}`, {
          replace: true,
          state: {
            flashMessage: t(
              'hostLessonOptions.createdAssignError',
              'Lesson option saved as draft, but some tutor assignments failed. Review and save again.'
            ),
            flashSeverity: 'error',
          },
        });
        return;
      }

      if (mode === 'activate') {
        try {
          await hostApiClient.activateLessonOption(created.id);
        } catch (activateError) {
          console.error(`Error activating new lesson option ${created.id}:`, activateError);
          navigate(`/host/lesson-options/${created.id}`, {
            replace: true,
            state: {
              flashMessage: t(
                'hostLessonOptions.createdActivateError',
                'Lesson option saved, but activation failed. Activate it from Lessons when you’re ready.'
              ),
              flashSeverity: 'error',
            },
          });
          return;
        }
      }

      navigate(`/host/lesson-options/${created.id}`, {
        replace: true,
        state: {
          flashMessage:
            mode === 'activate'
              ? t(
                  'hostLessonOptions.createdAndActivated',
                  'Lesson option created and activated'
                )
              : t(
                  'hostLessonOptions.createdAsDraft',
                  'Lesson option created as a draft'
                ),
        },
      });
    } catch (err) {
      const serverMessage = extractTutorProfileValidationMessage(err);
      if (mode === 'activate') {
        setError(
          serverMessage || t('hostLessonOptions.activateError', 'Failed to activate lesson option')
        );
      } else if (mode === 'draft' && status === 'ACTIVE') {
        setError(
          serverMessage ||
            t('hostLessonOptions.deactivateError', 'Failed to deactivate lesson option')
        );
      } else {
        setError(
          serverMessage ||
            (isEditMode
              ? t('hostLessonOptions.updateError', 'Failed to update lesson option')
              : t('hostLessonOptions.createError', 'Failed to create lesson option'))
        );
      }
    } finally {
      setSaving(false);
      setSavingMode(null);
    }
  };

  const handleToggleAssignment = (tutorId: string, assigned: boolean) => {
    setAssignedTutorIds((prev) => {
      if (assigned) {
        return prev.includes(tutorId) ? prev : [...prev, tutorId];
      }
      return prev.filter((id) => id !== tutorId);
    });
  };

  const assignmentRows = useMemo(() => {
    const assignedSet = new Set(assignedTutorIds);
    return tutors
      .filter((tutor) => assignedSet.has(tutor.tutorId) || isAssignableTutorStatus(tutor.status))
      .sort((a, b) => hostTutorDisplayName(a).localeCompare(hostTutorDisplayName(b)));
  }, [assignedTutorIds, tutors]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, pb: { xs: 8, md: 10 }, maxWidth: 800, width: '100%', mx: 'auto' }}>
      <Chip
        label={`← ${
          isEditMode
            ? t('hostLessonOptions.details.backToLesson', 'Back to lesson')
            : t('hostLessonOptions.backToCatalog', 'Back to lessons')
        }`}
        onClick={() => navigate(isEditMode && id ? `/host/lesson-options/${id}` : '/host/lesson-options')}
        sx={{
          mb: { xs: 1, sm: 2 },
          fontSize: { xs: '0.75rem', sm: '0.875rem' },
          bgcolor: 'transparent',
          color: 'text.primary',
          border: '1px solid',
          borderColor: 'divider',
          '&:hover': { bgcolor: 'transparent' },
        }}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {showDuplicateWarning && duplicateWarning && (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setShowDuplicateWarning(false)}>
          {duplicateWarning}
        </Alert>
      )}

      <Card elevation={3} sx={{ p: { xs: 2, md: 3 } }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 1,
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Typography variant="h4" component="h1">
            {isEditMode
              ? t('hostLessonOptions.editTitle', 'Edit lesson')
              : t('hostLessonOptions.createTitle', 'Create lesson')}
          </Typography>
          {isEditMode && status && <LessonOptionStatusChip status={status} />}
        </Box>
        {isEditMode && status === 'ARCHIVED' ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            {t(
              'hostLessonOptions.archivedReadOnlyHint',
              'This option is archived and can’t be edited. Manage it from the Archive tab.'
            )}
          </Alert>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {isIndividualTutorOrg
              ? t(
                  'hostLessonOptions.editHintIndividualTutor',
                  'Students only see this option when it is active.'
                )
              : t(
                  'hostLessonOptions.editHint',
                  'Students only see this option when it is active and assigned to the tutor they book.'
                )}
          </Typography>
        )}
        <Divider sx={{ mb: 3 }} />

        <Stack spacing={3}>
          <TextField
            fullWidth
            required
            label={t('hostLessonOptions.name', 'Name')}
            value={form.name}
            onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))}
            onBlur={() => setTouched((prev) => ({ ...prev, name: true }))}
            error={shouldShowFieldError('name')}
            helperText={shouldShowFieldError('name') ? fieldErrors.name : undefined}
          />
          <TextField
            fullWidth
            multiline
            minRows={2}
            label={t('hostLessonOptions.description', 'Description')}
            value={form.description}
            onChange={(event) => setForm((prev) => ({ ...prev, description: event.target.value }))}
          />
          <Box>
            <FormLabel sx={{ mb: 1, display: 'block' }}>
              {t('hostLessonOptions.sessionType', 'Session')}
            </FormLabel>
            <ToggleButtonGroup
              exclusive
              fullWidth
              size="small"
              value={form.sessionType}
              onChange={(_event, value: LessonOptionSessionType | null) => {
                if (!value) return;
                setForm((prev) => ({ ...prev, sessionType: value }));
                setTouched((prev) => ({ ...prev, sessionType: true }));
              }}
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' },
                gap: 1,
                '& .MuiToggleButtonGroup-grouped': {
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: '8px !important',
                  textTransform: 'none',
                  fontWeight: 600,
                },
              }}
            >
              <ToggleButton value="ONLINE">{getTutorSessionTypeLabel('ONLINE', t)}</ToggleButton>
              <ToggleButton value="AT_TEACHER">{getTutorSessionTypeLabel('AT_TEACHER', t)}</ToggleButton>
              <ToggleButton value="AT_STUDENT">{getTutorSessionTypeLabel('AT_STUDENT', t)}</ToggleButton>
            </ToggleButtonGroup>
            {shouldShowFieldError('sessionType') ? (
              <Typography variant="caption" color="error" sx={{ mt: 0.75, display: 'block' }}>
                {fieldErrors.sessionType}
              </Typography>
            ) : null}
          </Box>
          <Box
            sx={{
              display: 'grid',
              gap: 2,
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 110px' },
            }}
          >
            <FormControl fullWidth required error={shouldShowFieldError('durationMinutes')}>
              <TextField
                select
                required
                label={t('hostLessonOptions.duration', 'Duration')}
                value={form.durationMinutes}
                onChange={(event) =>
                  setForm((prev) => ({
                    ...prev,
                    durationMinutes: Number(event.target.value) as LessonOptionDurationMinutes,
                  }))
                }
                onBlur={() => setTouched((prev) => ({ ...prev, durationMinutes: true }))}
                error={shouldShowFieldError('durationMinutes')}
                helperText={
                  shouldShowFieldError('durationMinutes')
                    ? fieldErrors.durationMinutes
                    : t('hostLessonOptions.durationHint', 'Allowed: 30, 45, 60, 90, 120 minutes')
                }
              >
                {LESSON_OPTION_DURATION_MINUTES.map((minutes) => (
                  <MenuItem key={minutes} value={minutes}>
                    {t('hostLessonOptions.durationMinutes', '{{count}} min', { count: minutes })}
                  </MenuItem>
                ))}
              </TextField>
            </FormControl>
            <TextField
              required
              type="number"
              label={t('hostLessonOptions.price', 'Price')}
              value={form.price}
              onChange={(event) => setForm((prev) => ({ ...prev, price: event.target.value }))}
              onBlur={() => setTouched((prev) => ({ ...prev, price: true }))}
              error={shouldShowFieldError('price')}
              helperText={shouldShowFieldError('price') ? fieldErrors.price : undefined}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">{form.currencyCode || FALLBACK_CURRENCY}</InputAdornment>
                ),
              }}
              inputProps={{ min: 0, step: 0.01 }}
            />
            <TextField
              label={t('hostLessonOptions.currency', 'Currency')}
              value={form.currencyCode}
              onChange={(event) => setForm((prev) => ({ ...prev, currencyCode: event.target.value }))}
            />
          </Box>

          {!isIndividualTutorOrg ? (
            <Box>
              <Typography variant="h6" sx={{ mb: 0.5 }}>
                {t('hostLessonOptions.assignmentTitle', 'Assigned tutors')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                {t(
                  'hostLessonOptions.assignmentHint',
                  'Only assigned tutors can be booked for this option. Assignment changes apply when you save.'
                )}
              </Typography>
              {tutorsLoading ? (
                <Box display="flex" justifyContent="center" py={3}>
                  <CircularProgress size={28} />
                </Box>
              ) : assignmentRows.length === 0 ? (
                <Alert severity="info">
                  {t(
                    'hostLessonOptions.noTutors',
                    'No tutors on your roster yet. Add tutors first, then assign them here.'
                  )}
                </Alert>
              ) : (
                <Stack spacing={1}>
                  {assignmentRows.map((tutor) => {
                    const assigned = assignedTutorIds.includes(tutor.tutorId);
                    const displayName = hostTutorDisplayName(tutor);
                    return (
                      <Box
                        key={tutor.tutorId}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          px: 1.5,
                          py: 1,
                          borderRadius: 1.5,
                          border: 1,
                          borderColor: assigned ? 'primary.main' : 'divider',
                          bgcolor: (theme) =>
                            assigned ? alpha(theme.palette.primary.main, 0.06) : 'transparent',
                        }}
                      >
                        <Avatar src={tutor.profileImageUrl ?? undefined} sx={{ width: 36, height: 36 }}>
                          {tutorAvatarInitials(displayName)}
                        </Avatar>
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography variant="subtitle2" noWrap>
                            {displayName}
                          </Typography>
                          <Box sx={{ mt: 0.25 }}>
                            <HostTutorSpaceAssignmentField
                              tutorId={tutor.tutorId}
                              spaceId={tutor.spaceId}
                              spaceName={tutor.spaceName}
                              spaces={[]}
                            />
                          </Box>
                        </Box>
                        <Switch
                          checked={assigned}
                          disabled={saving || status === 'ARCHIVED'}
                          onChange={(event) =>
                            handleToggleAssignment(tutor.tutorId, event.target.checked)
                          }
                          inputProps={{
                            'aria-label': t('hostLessonOptions.assignTutorAria', 'Assign {{name}}', {
                              name: displayName,
                            }),
                          }}
                        />
                      </Box>
                    );
                  })}
                </Stack>
              )}
            </Box>
          ) : null}

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            justifyContent="space-between"
            alignItems={{ xs: 'stretch', sm: 'center' }}
          >
            <Button
              variant="outlined"
              onClick={() => navigate(isEditMode && id ? `/host/lesson-options/${id}` : '/host/lesson-options')}
              disabled={saving}
            >
              {t('common.cancel', 'Cancel')}
            </Button>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            >
              <Button
                variant="outlined"
                color="primary"
                onClick={() => handleSave('draft')}
                disabled={saving || status === 'ARCHIVED'}
              >
                {savingMode === 'draft' ? (
                  <CircularProgress size={20} />
                ) : (
                  t('hostLessonOptions.saveAsDraft', 'Save as draft')
                )}
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={() => handleSave('activate')}
                disabled={saving || status === 'ARCHIVED'}
              >
                {savingMode === 'activate' ? (
                  <CircularProgress size={20} />
                ) : (
                  t('hostLessonOptions.saveAndActivate', 'Save and activate')
                )}
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </Card>

      <NotificationSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      />
    </Box>
  );
};

export default HostLessonOptionFormPage;
